import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  fetchAtendimentosExternos,
  fetchCoberturaOficinaExterna,
  fetchOficinasExternas,
  fetchRegioesExternas,
  type AtendimentoExterno,
  type Cobertura,
  type OficinaExterna,
  type RegiaoExterna,
} from '../../../api/oficinaExternaApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import Paginacao from '../../../components/paginacao/Paginacao';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { notify } from '../../../shared/feedback/notify';
import SearchIcon from '../../../img/search-icon.svg';
import FilterIcon from '../../../img/filter.svg';
import AtendimentoExternoModal from '../components/AtendimentoExternoModal';
import RelatorioOficinaExternaModal from '../components/RelatorioOficinaExternaModal';
import { useAlcanca } from '../../../hooks/usePapeis';
import FiltrosOficinaExternaModal, {
  type FiltrosOficinaExterna,
} from '../components/FiltrosOficinaExternaModal';
import { dataCurta, diasSemVisitaTexto, seloDeAtraso } from '../formatacao';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/ui/sub-abas.css';
// Casco, tabela (`TabelaScroll`, `Tabela`, `Item`, `Data`), controles (`VisualizarOrdenar`,
// `CtaBig`) e abas são as classes compartilhadas do sistema — import explícito, porque contar
// com o CSS carregado por outra tela é acoplamento invisível (BUG-V74).
import '../../recepcao/pages/Recepcao.css';
import './OficinaExterna.css';

type Aba = 'VEICULOS' | 'COBERTURA';
/** Dentro da Cobertura: uma tabela por vez (decisão do Victor, 25/09/2026). */
type SubAbaCobertura = 'OFICINAS' | 'PLACAS';

const headersVeiculos = [
  'Placa',
  'Evento',
  'Oficina',
  'Região',
  'Etapa informada',
  'Previsão da etapa',
  'Previsão de entrega',
  'Última visita',
  'Entrada',
  'Saída',
];
const largurasVeiculos = [110, 100, 190, 140, 170, 150, 160, 150, 120, 120];

const headersOficinas = ['Oficina', 'Região', 'Veículos lá', 'Sem visita recente', 'Última visita'];
const largurasOficinas = [220, 160, 130, 170, 160];

const headersPlacas = ['Placa', 'Oficina', 'Região', 'Entrada', 'Última visita'];
const largurasPlacas = [110, 220, 160, 130, 160];

const FILTROS_PADRAO: FiltrosOficinaExterna = {
  situacao: 'NA_OFICINA',
  regiaoId: '',
  oficinaId: '',
};

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta tela mostra',
    conteudo: (
      <>
        <p>
          Os veículos que a Atual acompanha em <strong>oficinas de terceiros</strong>. Quem vai até
          lá é o <strong>inspetor</strong>: ele passa nas oficinas, vê como está cada carro e
          registra a <strong>visita</strong>. Esta tela é a leitura desse registro.
        </p>
        <p>
          É um ambiente separado da oficina interna: lá existem esteiras, colaboradores e horas
          apontadas; aqui existe visita. Um mesmo veículo pode ter passado pelos dois — o vínculo
          entre eles é a <strong>placa e o número do evento</strong>.
        </p>
      </>
    ),
  },
  {
    titulo: 'De onde vêm a etapa e a previsão',
    conteudo: (
      <>
        <p>
          As duas são <strong>informadas pela oficina ao inspetor</strong> no dia da visita, e
          registradas como ele ouviu. <strong>Não há acompanhamento em tempo real</strong>: a
          oficina não é nossa, e ninguém lá aponta serviço no sistema.
        </p>
        <p>
          Por isso os dois campos podem estar desatualizados entre uma visita e outra — a data da
          última visita, ao lado, é o que diz de quando aquela informação é.
        </p>
      </>
    ),
  },
  {
    titulo: 'O que "dias sem visita" mede',
    conteudo: (
      <>
        <p>
          A diferença entre hoje e a <strong>data da última visita informada pelo inspetor</strong>{' '}
          — não a data em que o registro chegou ao sistema. Os dois podem ser diferentes: o
          inspetor trabalha com o celular sem rede e o registro sobe depois, quando ele volta a ter
          sinal.
        </p>
        <p>
          <strong>Nunca visitado</strong> é diferente de "visitado há muitos dias": significa que
          aquele veículo foi cadastrado na oficina e ninguém registrou visita nenhuma nele ainda.
          Por isso ele aparece em primeiro lugar na Cobertura.
        </p>
      </>
    ),
  },
  {
    titulo: 'Os filtros',
    conteudo: (
      <>
        <p>
          Ficam no botão <strong>Filtrar</strong>, e só são aplicados quando você toca em{' '}
          <strong>Aplicar Filtros</strong> — ajustar vários de uma vez faz uma consulta só.
          Reabrindo o modal, ele mostra o que está valendo.
        </p>
        <p>
          Com o modal fechado, o indício de que a lista está filtrada é o{' '}
          <strong>contador de veículos</strong> ao lado. Para conferir ou desfazer, reabra o{' '}
          <strong>Filtrar</strong>.
        </p>
      </>
    ),
  },
  {
    titulo: 'A aba Cobertura',
    conteudo: (
      <>
        <p>
          Mostra o que está <strong>há mais tempo sem visita</strong>, por oficina e por placa.
          Como os inspetores <strong>não têm rota fixa</strong> — qualquer um visita qualquer
          oficina —, é esta lista que orienta para onde ir.
        </p>
        <p>
          A partir de quantos dias o veículo aparece sinalizado é definido em{' '}
          <strong>Administração do Sistema → Oficina Externa</strong>.
        </p>
        <p>
          Quando um veículo passa desse limite, o <strong>Chefe de Oficina</strong>, o{' '}
          <strong>setor de Eventos</strong> e os administradores recebem um aviso no sino, às 8h.
          O aviso sai <strong>uma vez</strong> por veículo: só volta a sair depois de uma visita
          nova, se o veículo passar do limite de novo. Clicar no aviso abre esta tela já buscando
          a placa.
        </p>
      </>
    ),
  },
  {
    titulo: 'Gerar relatório',
    conteudo: (
      <>
        <p>
          O botão <strong>Gerar relatório</strong> exporta em <strong>CSV</strong> (planilha) ou{' '}
          <strong>PDF</strong>, de dois jeitos: <strong>Visitas</strong>, uma linha por visita, com
          as respostas do checklist; e <strong>Veículos</strong>, uma linha por veículo, inclusive
          o que nunca foi visitado.
        </p>
        <p>
          No modo Visitas o período é a <strong>data informada da visita</strong>, não a data em
          que ela chegou ao sistema. No modo Veículos, ele traz os veículos que estiveram na
          oficina externa em algum dia do período. O PDF diz no topo quais filtros foram usados.
        </p>
      </>
    ),
  },
  {
    titulo: 'O que esta tela NÃO faz',
    conteudo: (
      <>
        Ela é <strong>somente de consulta</strong>. Cadastrar o veículo na oficina, registrar a
        visita e registrar a saída são ações do inspetor, feitas na tela dele. Se um dado estiver
        errado aqui, o caminho é falar com quem fez a visita — o registro guarda o nome e a data, e
        não é editável: correção se faz com uma visita nova.
      </>
    ),
  },
];

/**
 * Veículos em Oficina Externa (at-114, ADR 0059) — Chefe de Oficina e setor de Eventos (ADMIN e
 * TI em leitura).
 *
 * 🔴 A regra da at-60 vale em dobro nas duas abas: "nenhum veículo em oficina externa" e "tudo
 * visitado" são AFIRMAÇÕES sobre a operação, e cada uma delas, dita por engano depois de uma
 * falha de carga, leva a uma decisão errada — mover um carro que não está no pátio, ou não ir a
 * uma oficina que está há duas semanas sem visita.
 */
const OficinasExternasLeituraPage: React.FC = () => {
  const [aba, setAba] = useState<Aba>('VEICULOS');
  const [subAba, setSubAba] = useState<SubAbaCobertura>('OFICINAS');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [filtroAberto, setFiltroAberto] = useState(false);

  const [termo, setTermo] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [filtros, setFiltros] = useState<FiltrosOficinaExterna>(FILTROS_PADRAO);

  const [itens, setItens] = useState<AtendimentoExterno[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(20);
  const [diasAlerta, setDiasAlerta] = useState(3);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /** `false` enquanto a consulta atual não tiver respondido com sucesso. */
  const [respondeu, setRespondeu] = useState(false);

  const [cobertura, setCobertura] = useState<Cobertura | null>(null);
  const [carregandoCobertura, setCarregandoCobertura] = useState(false);
  const [erroCobertura, setErroCobertura] = useState<string | null>(null);

  const [regioes, setRegioes] = useState<RegiaoExterna[]>([]);
  const [oficinas, setOficinas] = useState<OficinaExterna[]>([]);
  const [atendimentoAberto, setAtendimentoAberto] = useState<AtendimentoExterno | null>(null);
  const [relatorioAberto, setRelatorioAberto] = useState(false);
  // Os mesmos papéis do `@Roles` da rota do relatório; o ADMIN entra pelo bypass do `alcanca`.
  const podeGerarRelatorio = useAlcanca(['CHEFE_DE_OFICINA', 'EVENTOS']);

  const tabelaRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const oficinasRef = useRef<HTMLDivElement>(null);
  const oficinasHeaderRefs = useRef<(HTMLDivElement | null)[]>([]);
  const placasRef = useRef<HTMLDivElement>(null);
  const placasHeaderRefs = useRef<(HTMLDivElement | null)[]>([]);

  /*
   * at-132 — o aviso de "sem visita" aberto pelo sino do topo chega como `?placa=` (molde do
   * Andamento dos Veículos): abre a aba Veículos já buscando a placa. O parâmetro sai da URL.
   */
  const [parametrosDaUrl, setParametrosDaUrl] = useSearchParams();
  useEffect(() => {
    const placa = parametrosDaUrl.get('placa');
    if (!placa) return;
    setParametrosDaUrl({}, { replace: true });
    setAba('VEICULOS');
    setTermo(placa);
    setPagina(1);
    setBuscaAplicada(placa);
  }, [parametrosDaUrl, setParametrosDaUrl]);

  // Cadastros para o modal de filtros. Falha aqui não impede a listagem — só deixa o modal sem
  // opções, e o aviso diz isso em vez de fingir que não há região nenhuma cadastrada.
  useEffect(() => {
    let ativo = true;
    void (async () => {
      try {
        const [rs, os] = await Promise.all([fetchRegioesExternas(), fetchOficinasExternas()]);
        if (!ativo) return;
        setRegioes(rs);
        setOficinas(os);
      } catch (error) {
        if (!ativo) return;
        notify.errorFrom(
          error,
          'Não foi possível carregar regiões e oficinas para os filtros. A lista de veículos ' +
            'continua funcionando; recarregue a página se precisar filtrar.',
        );
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const consultar = useCallback(async () => {
    setCarregando(true);
    try {
      const resposta = await fetchAtendimentosExternos({
        placa: buscaAplicada || undefined,
        situacao: filtros.situacao,
        regiaoId: filtros.regiaoId || undefined,
        oficinaId: filtros.oficinaId || undefined,
        page: pagina,
        limit: limite,
      });
      setItens(resposta.itens ?? []);
      setTotal(resposta.total ?? 0);
      setDiasAlerta(resposta.diasSemVisitaAlerta ?? 3);
      setErro(null);
      setRespondeu(true);
    } catch {
      // Estado seguro E sinal visível: a lista esvazia, mas `respondeu` fica false, então a tela
      // NÃO afirma que não há veículo em oficina externa.
      setItens([]);
      setTotal(0);
      setRespondeu(false);
      setErro(
        'Não foi possível consultar os veículos em oficina externa. Enquanto este aviso estiver ' +
          'na tela, a lista não serve como resposta — recarregue a página e, se persistir, ' +
          'acione a TI.',
      );
      // TEXTO FIXO, não `errorFrom` (regra da at-65): o aviso é sobre o ESTADO ENGANOSO DA TELA,
      // não sobre uma ação que falhou.
      notify.error(
        'Não foi possível consultar os veículos em oficina externa. Não conclua que um veículo ' +
          'não está fora enquanto este aviso aparecer.',
        { toastId: 'oficina-externa-lista-falhou' },
      );
    } finally {
      setCarregando(false);
    }
  }, [buscaAplicada, filtros, pagina, limite]);

  useEffect(() => {
    if (aba === 'VEICULOS') void consultar();
  }, [aba, consultar]);

  const consultarCobertura = useCallback(async () => {
    setCarregandoCobertura(true);
    try {
      const resposta = await fetchCoberturaOficinaExterna(filtros.regiaoId || undefined);
      setCobertura(resposta);
      setErroCobertura(null);
    } catch {
      // 🔴 `null`, nunca `{ oficinas: [], placas: [] }`: lista vazia aqui seria lida como "tudo
      // visitado", que é o oposto do que a falha significa.
      setCobertura(null);
      setErroCobertura(
        'Não foi possível verificar a cobertura das visitas. Esta tela NÃO está dizendo que tudo ' +
          'foi visitado — ela não conseguiu consultar. Recarregue a página; se persistir, acione a TI.',
      );
      notify.error('Não foi possível verificar a cobertura das visitas.', {
        toastId: 'oficina-externa-cobertura-falhou',
      });
    } finally {
      setCarregandoCobertura(false);
    }
  }, [filtros.regiaoId]);

  useEffect(() => {
    if (aba === 'COBERTURA') void consultarCobertura();
  }, [aba, consultarCobertura]);

  const aplicarBusca = () => {
    setPagina(1);
    setBuscaAplicada(termo.trim());
  };

  const limparBusca = () => {
    setTermo('');
    setPagina(1);
    setBuscaAplicada('');
  };

  const linhasVeiculos = useMemo(
    () =>
      itens.map((a) => [
        a.placa,
        String(a.evento?.eventNumber ?? '-'),
        a.oficina?.nome ?? '-',
        a.oficina?.regiao?.nome ?? '-',
        a.etapaAtual?.nome ?? '-',
        dataCurta(a.previsaoEtapa),
        dataCurta(a.previsaoEntrega),
        diasSemVisitaTexto(a.diasSemVisita),
        dataCurta(a.entradaEm),
        dataCurta(a.saidaEm),
      ]),
    [itens],
  );

  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tabelaRef,
    headerCellsRef: headerRefs,
    headers: headersVeiculos,
    defaultWidths: largurasVeiculos,
    rows: linhasVeiculos,
    enabled: aba === 'VEICULOS',
    remeasureKey: aba,
  });

  const linhasOficinas = useMemo(
    () =>
      (cobertura?.oficinas ?? []).map((o) => [
        o.nome,
        o.regiao?.nome ?? '-',
        String(o.atendimentosAtivos),
        String(o.placasSemVisitaRecente),
        diasSemVisitaTexto(o.diasSemVisita),
      ]),
    [cobertura],
  );

  const colunasOficinas = useTableColumns({
    containerRef: oficinasRef,
    headerCellsRef: oficinasHeaderRefs,
    headers: headersOficinas,
    defaultWidths: largurasOficinas,
    rows: linhasOficinas,
    enabled: aba === 'COBERTURA' && subAba === 'OFICINAS',
    // A troca de sub-aba remonta o container, e o ResizeObserver precisa religar no novo.
    remeasureKey: `${aba}-${subAba}`,
  });

  const linhasPlacas = useMemo(
    () =>
      (cobertura?.placas ?? []).map((p) => [
        p.placa,
        p.oficina?.nome ?? '-',
        p.oficina?.regiao?.nome ?? '-',
        dataCurta(p.entradaEm),
        diasSemVisitaTexto(p.diasSemVisita),
      ]),
    [cobertura],
  );

  const colunasPlacas = useTableColumns({
    containerRef: placasRef,
    headerCellsRef: placasHeaderRefs,
    headers: headersPlacas,
    defaultWidths: largurasPlacas,
    rows: linhasPlacas,
    enabled: aba === 'COBERTURA' && subAba === 'PLACAS',
    remeasureKey: `${aba}-${subAba}`,
  });

  const totalPaginas = Math.max(1, Math.ceil(total / limite));
  const primeiroDaPagina = total === 0 ? 0 : (pagina - 1) * limite + 1;
  const ultimoDaPagina = Math.min(pagina * limite, total);

  const textoDoContador = (): string => {
    if (carregando) return 'Consultando...';
    if (!respondeu) return 'Não foi possível consultar';
    if (total === 0) return '0 veículos';
    return `${primeiroDaPagina}-${ultimoDaPagina} de ${total} veículos`;
  };

  /** Casca branca das tabelas — a mesma medida das demais telas do sistema. */
  const molduraDaTabela: React.CSSProperties = {
    width: '100%',
    paddingTop: 30,
    background: 'white',
    borderRadius: 16,
    border: '1px #CACACA solid',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    display: 'flex',
    marginBottom: 20,
  };

  return (
    <div className="ConteDo recepcao-conte-do">
      {aba === 'VEICULOS' && erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}
      {aba === 'COBERTURA' && erroCobertura && (
        <div className="erro-de-tela" role="alert">
          {erroCobertura}
        </div>
      )}

      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Veículos em Oficina Externa
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>

        {aba === 'VEICULOS' && (
          <div className="recepcao-search-bar">
            <div className="ItemSearch" onClick={aplicarBusca}>
              <img className="imgIcon" src={SearchIcon} alt="Ícone de busca" width="20" height="20" />
            </div>
            <input
              type="text"
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && aplicarBusca()}
              placeholder="Busque pela placa"
              aria-label="Buscar placa"
              className="recepcao-search-input"
            />
          </div>
        )}
      </div>

      <div className="recepcao-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'VEICULOS'}
          className={`recepcao-tab ${aba === 'VEICULOS' ? 'active' : ''}`}
          onClick={() => setAba('VEICULOS')}
        >
          Veículos
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={aba === 'COBERTURA'}
          className={`recepcao-tab ${aba === 'COBERTURA' ? 'active' : ''}`}
          onClick={() => setAba('COBERTURA')}
        >
          Cobertura
        </button>
      </div>

      {/* A explicação fica FIXA na tela, não só na hint: é a leitura errada mais provável da
          tabela, e quem consulta de relance não abre o "Como Funciona". */}
      <p className="oe-hint">
        A <strong>etapa</strong> e as <strong>duas previsões</strong> (da etapa atual e da
        entrega total) são o que a oficina informou ao inspetor na última visita — não há acompanhamento em tempo real. A data da última visita
        diz de quando essa informação é.
      </p>

      {aba === 'VEICULOS' && (
        <>
          <div className="VisualizarOrdenar">
            <div
              className="Frame1000004037"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              <div className="CtaBig">
                <div className="Frame1000003987">
                  <div className="ComponentName">Itens por página:</div>
                  <select
                    value={limite}
                    onChange={(e) => {
                      setLimite(Number(e.target.value));
                      setPagina(1);
                    }}
                    className="ComponentNameImgIcon"
                    style={{ cursor: 'pointer' }}
                    aria-label="Itens por página"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              <div className="CtaBig MouseHover" onClick={() => setFiltroAberto(true)}>
                <div className="Frame1000003987">
                  <img src={FilterIcon} alt="Filtro" width="19.8" height="19.8" />
                  <div className="ComponentName">Filtrar</div>
                </div>
              </div>

              {/* at-132: o TI não gera relatório (ação operacional, ADR 0012). */}
              {podeGerarRelatorio && (
                <div
                  className="CtaBig MouseHover"
                  role="button"
                  tabIndex={0}
                  onClick={() => setRelatorioAberto(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setRelatorioAberto(true)}
                >
                  <div className="Frame1000003987">
                    <div className="ComponentName">Gerar relatório</div>
                  </div>
                </div>
              )}

              {buscaAplicada && (
                <div className="CtaBig MouseHover" onClick={limparBusca}>
                  <div className="Frame1000003987">
                    <div className="ComponentName">Limpar busca</div>
                  </div>
                </div>
              )}
            </div>

            <div className="QuantidadeDeCarros">
              <div
                className="Frame1000004037"
                style={{
                  alignSelf: 'stretch',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 12,
                  display: 'flex',
                }}
              >
                <div className="CtaQuantidadeDeCarros">
                  <div className="Frame1000003987">
                    <div className="ComponentName">{textoDoContador()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={molduraDaTabela}>
            <div className="TabelaScroll" ref={tabelaRef}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as any]: tableColumns,
                  width: tableWidth,
                  minWidth: tableWidth,
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      {headersVeiculos.map((header, indice) => (
                        <div
                          key={header}
                          className="Opcoes"
                          ref={(el) => {
                            headerRefs.current[indice] = el;
                          }}
                        >
                          {header}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {carregando && (
                  <div className="Tabela">
                    <div className="Item" style={{ gridColumn: `1 / span ${headersVeiculos.length}` }}>
                      <div className="Data">Consultando...</div>
                    </div>
                  </div>
                )}

                {/* "Nenhum veículo" é afirmação de negócio: só quando a consulta respondeu. */}
                {!carregando && itens.length === 0 && respondeu && (
                  <div className="Tabela">
                    <div className="Item" style={{ gridColumn: `1 / span ${headersVeiculos.length}` }}>
                      <div className="Data">
                        {buscaAplicada
                          ? 'Nenhum veículo em oficina externa encontrado para esta placa.'
                          : 'Nenhum veículo em oficina externa nesta situação.'}
                      </div>
                    </div>
                  </div>
                )}

                {!carregando && itens.length === 0 && !respondeu && (
                  <div className="Tabela">
                    <div className="Item" style={{ gridColumn: `1 / span ${headersVeiculos.length}` }}>
                      <div className="Data">A consulta não pôde ser feita — veja o aviso acima.</div>
                    </div>
                  </div>
                )}

                {!carregando &&
                  itens.map((a) => {
                    const selo = seloDeAtraso(a.diasSemVisita, diasAlerta);
                    return (
                      <div key={a.id} className="Tabela">
                        <div className="Item">
                          {/* ⚠️ A classe `Data` é obrigatória no botão: é ela que carrega a
                              tipografia da célula. Um <button> não herda fonte do pai. */}
                          <button
                            type="button"
                            className="Data oe-placa-btn"
                            title={`Ver as visitas de ${a.placa}`}
                            onClick={() => setAtendimentoAberto(a)}
                          >
                            {a.placa}
                          </button>
                        </div>
                        <div className="Item">
                          <div className="Data">{a.evento?.eventNumber ?? '-'}</div>
                        </div>
                        <div className="Item">
                          <div className="Data" title={a.oficina?.nome}>
                            {a.oficina?.nome ?? '-'}
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data">{a.oficina?.regiao?.nome ?? '-'}</div>
                        </div>
                        <div className="Item">
                          <div
                            className="Data"
                            title={a.etapaAtual?.nome ?? 'Nenhuma etapa informada ainda'}
                          >
                            {a.etapaAtual?.nome ?? '-'}
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data" title="Quando a oficina disse que a etapa atual termina">
                            {dataCurta(a.previsaoEtapa)}
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data" title="Quando a oficina disse que o veículo fica pronto">
                            {dataCurta(a.previsaoEntrega)}
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data">
                            <span className={`cilia-badge ${selo.classe}`} title={selo.titulo}>
                              {diasSemVisitaTexto(a.diasSemVisita)}
                            </span>
                          </div>
                        </div>
                        <div className="Item">
                          <div className="Data">{dataCurta(a.entradaEm)}</div>
                        </div>
                        <div className="Item">
                          <div className="Data">{dataCurta(a.saidaEm)}</div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          {totalPaginas > 1 && (
            <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
          )}
        </>
      )}

      {aba === 'COBERTURA' && (
        <>
          {carregandoCobertura && <p className="oe-hint">Consultando...</p>}

          {/* Sem `cobertura` NADA é renderizado além do aviso: listas vazias seriam lidas como
              "tudo visitado". */}
          {!carregandoCobertura && cobertura && (
            <>
              <p className="oe-hint">
                Sinalizamos o que está sem visita há <strong>{cobertura.diasAlerta} dias</strong> ou
                mais — o limiar é definido em Administração do Sistema → Oficina Externa. Quem
                nunca foi visitado vem primeiro.
              </p>

              {/* Uma tabela por vez (decisão do Victor, 25/09/2026): duas empilhadas na mesma
                  tela competiam entre si e obrigavam a rolar para comparar. As sub-abas usam o
                  estilo compartilhado `shared/ui/sub-abas.css`, em escala menor que as abas
                  principais — duas fileiras do mesmo tamanho não diriam qual está dentro de qual. */}
              <div className="sub-abas" role="tablist" aria-label="Cobertura por">
                <button
                  type="button"
                  role="tab"
                  aria-selected={subAba === 'OFICINAS'}
                  className={`sub-aba ${subAba === 'OFICINAS' ? 'active' : ''}`}
                  onClick={() => setSubAba('OFICINAS')}
                >
                  Por oficina
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={subAba === 'PLACAS'}
                  className={`sub-aba ${subAba === 'PLACAS' ? 'active' : ''}`}
                  onClick={() => setSubAba('PLACAS')}
                >
                  Por veículo
                </button>
              </div>

              {/* Montagem condicional, nunca `hidden`/`display: none`: a tabela escondida
                  continuaria no DOM (o leitor de tela leria as duas) e o `useTableColumns` da
                  inativa mediria largura zero. É a mesma razão do `blocosDoModo` do Dashboard de
                  Compras — bloco que não é desenhado também não é medido. */}
              {subAba === 'OFICINAS' && (
              <div style={molduraDaTabela}>
                <div className="TabelaScroll" ref={oficinasRef}>
                  <div
                    className="TabelaContent"
                    style={{
                      ['--table-columns' as any]: colunasOficinas.tableColumns,
                      width: colunasOficinas.tableWidth,
                      minWidth: colunasOficinas.tableWidth,
                    }}
                  >
                    <div className="Tabela TabelaHeader">
                      <div className="Placa">
                        <div className="TTulo">
                          {headersOficinas.map((header, indice) => (
                            <div
                              key={header}
                              className="Opcoes"
                              ref={(el) => {
                                oficinasHeaderRefs.current[indice] = el;
                              }}
                            >
                              {header}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {cobertura.oficinas.length === 0 && (
                      <div className="Tabela">
                        <div
                          className="Item"
                          style={{ gridColumn: `1 / span ${headersOficinas.length}` }}
                        >
                          <div className="Data">
                            Nenhum veículo em oficina externa no momento — não há o que visitar.
                          </div>
                        </div>
                      </div>
                    )}

                    {cobertura.oficinas.map((o) => {
                      const selo = seloDeAtraso(o.diasSemVisita, cobertura.diasAlerta);
                      return (
                        <div key={o.id} className="Tabela">
                          <div className="Item">
                            <div className="Data" title={o.nome}>
                              {o.nome}
                            </div>
                          </div>
                          <div className="Item">
                            <div className="Data">{o.regiao?.nome ?? '-'}</div>
                          </div>
                          <div className="Item">
                            <div className="Data">{o.atendimentosAtivos}</div>
                          </div>
                          <div className="Item">
                            <div className="Data">{o.placasSemVisitaRecente}</div>
                          </div>
                          <div className="Item">
                            <div className="Data">
                              <span className={`cilia-badge ${selo.classe}`} title={selo.titulo}>
                                {diasSemVisitaTexto(o.diasSemVisita)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              )}

              {subAba === 'PLACAS' && (
              <div style={molduraDaTabela}>
                <div className="TabelaScroll" ref={placasRef}>
                  <div
                    className="TabelaContent"
                    style={{
                      ['--table-columns' as any]: colunasPlacas.tableColumns,
                      width: colunasPlacas.tableWidth,
                      minWidth: colunasPlacas.tableWidth,
                    }}
                  >
                    <div className="Tabela TabelaHeader">
                      <div className="Placa">
                        <div className="TTulo">
                          {headersPlacas.map((header, indice) => (
                            <div
                              key={header}
                              className="Opcoes"
                              ref={(el) => {
                                placasHeaderRefs.current[indice] = el;
                              }}
                            >
                              {header}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {cobertura.placas.length === 0 && (
                      <div className="Tabela">
                        <div
                          className="Item"
                          style={{ gridColumn: `1 / span ${headersPlacas.length}` }}
                        >
                          <div className="Data">Nenhum veículo em oficina externa no momento.</div>
                        </div>
                      </div>
                    )}

                    {cobertura.placas.map((p) => {
                      const selo = seloDeAtraso(p.diasSemVisita, cobertura.diasAlerta);
                      return (
                        <div key={p.atendimentoId} className="Tabela">
                          <div className="Item">
                            <div className="Data">{p.placa}</div>
                          </div>
                          <div className="Item">
                            <div className="Data" title={p.oficina?.nome}>
                              {p.oficina?.nome ?? '-'}
                            </div>
                          </div>
                          <div className="Item">
                            <div className="Data">{p.oficina?.regiao?.nome ?? '-'}</div>
                          </div>
                          <div className="Item">
                            <div className="Data">{dataCurta(p.entradaEm)}</div>
                          </div>
                          <div className="Item">
                            <div className="Data">
                              <span className={`cilia-badge ${selo.classe}`} title={selo.titulo}>
                                {diasSemVisitaTexto(p.diasSemVisita)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              )}
            </>
          )}
        </>
      )}

      {/* Montagem condicional, nunca prop `isOpen` (at-65): é o que faz o rascunho descartado no
          "Cancelar" não sobreviver para a abertura seguinte. */}
      {filtroAberto && (
        <FiltrosOficinaExternaModal
          regioes={regioes}
          oficinas={oficinas}
          filtrosAplicados={filtros}
          onAplicar={(novos) => {
            setFiltros(novos);
            // Aplicar volta para a página 1: senão a página 3 do filtro anterior vira lista
            // vazia sem explicação.
            setPagina(1);
          }}
          onCancelar={() => setFiltroAberto(false)}
        />
      )}

      {relatorioAberto && (
        <RelatorioOficinaExternaModal
          regioes={regioes}
          oficinas={oficinas}
          onFechar={() => setRelatorioAberto(false)}
        />
      )}

      {atendimentoAberto && (
        <AtendimentoExternoModal
          atendimentoId={atendimentoAberto.id}
          placa={atendimentoAberto.placa}
          onClose={() => setAtendimentoAberto(null)}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Veículos em Oficina Externa"
        secoes={helpSections}
      />
    </div>
  );
};

export default OficinasExternasLeituraPage;
